import { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import { CalendarIcon } from '@navi/web-ui/lib/icons';
import { Typography } from '@navi/web-ui/lib/primitives';
import { useOutsideClick } from '@hooks';
import styles from './CalendarSelect.module.scss';
import { parseDateString } from '../../utils';
import CalendarPicker from './CalendarPicker';

interface CalendarSelectProps {
  value: string;
  onChange: (dates: string) => void;
  placeholder?: string;
  error?: string | boolean;
  required?: boolean;
}

const CalendarSelect = (props: CalendarSelectProps) => {
  const { value, onChange, placeholder, error, required } = props;

  const [showPicker, setShowPicker] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Set<number>>(new Set());

  const calendarRef = useRef<HTMLDivElement>(null);

  const hasValue = value && value.trim() !== '';

  useEffect(() => {
    if (hasValue) {
      const dates = parseDateString(value);
      setSelectedDates(new Set(dates));
    } else {
      setSelectedDates(new Set());
    }
  }, [value]);

  const handleDateClick = (day: number) => {
    const newDates = new Set(selectedDates);
    if (newDates.has(day)) {
      newDates.delete(day);
    } else {
      newDates.add(day);
    }
    const sortedDates = Array.from(newDates).sort((a, b) => a - b);
    const dateString = sortedDates.join(', ');
    onChange(dateString);
    setSelectedDates(newDates);
  };

  const handleClearAll = () => {
    setSelectedDates(new Set());
    onChange('');
    setShowPicker(false);
  };

  useOutsideClick(calendarRef, () => {
    setShowPicker(false);
  });

  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <div className="w-full relative" ref={calendarRef}>
      <div className="flex items-center gap-1">
        {required && <span className="text-red-500">*</span>}
      </div>

      <div
        className={cx(styles.inputContainer, {
          [styles.default]: !error,
          [styles.error]: !!error,
          [styles.open]: showPicker
        })}
        onClick={() => setShowPicker(!showPicker)}
      >
        <Typography
          variant="p3"
          className={cx(styles.inputText, { [styles.placeholder]: !hasValue })}
        >
          {hasValue ? value : placeholder}
        </Typography>
        <CalendarIcon size="sm" />
      </div>

      {error && (
        <Typography variant="p4" color="var(--navi-color-red-base)" className="mt-1">
          {error}
        </Typography>
      )}

      {showPicker && (
        <CalendarPicker
          days={days}
          selectedDates={selectedDates}
          onDateClick={handleDateClick}
          onClearAll={handleClearAll}
        />
      )}
    </div>
  );
};

export default CalendarSelect;
